import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { DataConnector, LongStringColumnComponent } from '@components/data-grid';
import { environment } from '@environments';
import {
  CreateRequestResponse,
  DataResponse,
  FilterDropdownOption,
  GetStatusResponse,
  MAX_COLUMN_WIDTH,
  PageSizeOptions,
} from '@models';
import { OnBehalfOfRequester, ValidationRequestResponse, ValidationResponse } from '@models/request-creation.models';
import { ValidateRequestRequest } from '@models/request-quick-create.models';
import { DateColumnComponent } from '@views';
import { ColDef } from 'ag-grid-community';
import { map, Observable } from 'rxjs';
import { downloadFileFromResponse } from '../../../utils/file-downloader';

@Injectable({
  providedIn: 'root',
})
export class ValidateRequestService {
  static readonly DEFAULT_PARAMS = {
    pageSize: PageSizeOptions[0].value,
    pageNumber: 1,
    sortExpression: `rowId asc`,
  };

  readonly #http = inject(HttpClient);

  validateRequest(requestId: string): Observable<ValidationResponse> {
    const url = `${environment.apiConfig.baseUrl}/create-request/${requestId}`;
    return this.#http.get<ValidationResponse>(url);
  }

  validateSimpleRequest(request: ValidateRequestRequest): Observable<ValidationResponse> {
    const url = `${environment.apiConfig.baseUrl}/create-request/validate-simple`;
    return this.#http.post<ValidationResponse>(url, request);
  }

  getRequestedProducts(requestId: string, params: Params): Observable<DataResponse<ValidationRequestResponse>> {
    const url = `${environment.apiConfig.baseUrl}/create-request/${requestId}/requested-products`;
    return this.#http.post<DataResponse<ValidationRequestResponse>>(url, this.buildRequestParams(params));
  }

  submitRequest(
    requestId: string,
    payload: { comment: string; onBehalfOf: string | null; requestCategory: string | null }
  ): Observable<CreateRequestResponse> {
    const url = `${environment.apiConfig.baseUrl}/create-request/${requestId}`;
    return this.#http.post<CreateRequestResponse>(url, payload);
  }

  getStatus(requestId: string): Observable<GetStatusResponse> {
    const url = `${environment.apiConfig.baseUrl}/create-request/${requestId}/status`;
    return this.#http.get<GetStatusResponse>(url).pipe(
      map(response => {
        return {
          ...response,
        };
      })
    );
  }

  getOnBehalfOfRequesters(params?: Params): Observable<FilterDropdownOption[]> {
    const url = `${environment.apiConfig.baseUrl}/create-request/obo-requesters`;
    return this.#http.post<DataResponse<OnBehalfOfRequester>>(url, this.#buildRequestParams(params)).pipe(
      map(response =>
        response.items.map(
          requester =>
            ({
              displayName: requester.fullname,
              key: requester.id,
              value: requester.id,
            }) as FilterDropdownOption
        )
      )
    );
  }

  #buildRequestParams(params?: Params): Params {
    const p = DataConnector.buildRequestParams(params);
    const result: Params = {
      pageSize: p?.['pageSize'] || ValidateRequestService.DEFAULT_PARAMS.pageSize,
      pageNumber: p?.['pageNumber'] || ValidateRequestService.DEFAULT_PARAMS.pageNumber,
      sortExpression: p?.['sortExpression'] || ValidateRequestService.DEFAULT_PARAMS.sortExpression,
    };

    if (p?.['filterGroups'] && p['filterGroups'].length > 0) {
      result['filterGroups'] = p['filterGroups'];
    }

    if (p?.['search'] && p['search'].trim() !== '') {
      result['search'] = p['search'];
    }

    return result;
  }

  getErrorFile(requestId: string) {
    const url = `${environment.apiConfig.baseUrl}/create-request/${requestId}/download`;
    return this.#http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map(response => {
        downloadFileFromResponse(response);
        return response.body!;
      })
    );
  }

  private buildRequestParams(params?: Params): Params {
    const p = DataConnector.buildRequestParams(params);
    const result: Params = {
      pageSize: p?.['pageSize'] || ValidateRequestService.DEFAULT_PARAMS.pageSize,
      pageNumber: p?.['pageNumber'] || ValidateRequestService.DEFAULT_PARAMS.pageNumber,
      sortExpression: p?.['sortExpression'] || ValidateRequestService.DEFAULT_PARAMS.sortExpression,
    };

    if (p?.['search'] && p['search'].trim() !== '') {
      result['search'] = p['search'];
    }

    return result;
  }

  public getColumnDefinitions(): ColDef[] {
    return [
      DataConnector.createGridColumn({ field: 'rowId', headerName: 'Row ID', width: 100 }),
      DataConnector.createGridColumn({
        field: 'productId',
        headerName: 'Product ID',
        width: 150,
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'productId' },
      }),
      DataConnector.createGridColumn({
        field: 'description',
        headerName: 'Description',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'description' },
        maxWidth: MAX_COLUMN_WIDTH,
      }),
      DataConnector.createGridColumn({
        field: 'classificationType',
        headerName: 'Classification Type',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'classificationType' },
        width: 150,
      }),
      DataConnector.createGridColumn({
        field: 'countryCode',
        headerName: 'Country Code',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'countryCode' },
        width: 130,
      }),
      DataConnector.createGridColumn({
        field: 'region',
        headerName: 'Region',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'region' },
        width: 130,
      }),
      DataConnector.createGridColumn({ field: 'priority', headerName: 'Priority', width: 100 }),
      DataConnector.createGridColumn({
        field: 'dueDate',
        headerName: 'Due Date',
        cellRenderer: DateColumnComponent,
        width: 180,
      }),
      DataConnector.createGridColumn({
        field: 'subDivision',
        headerName: 'Subdivision',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'subDivision' },
        width: 150,
      }),
      DataConnector.createGridColumn({
        field: 'comment',
        headerName: 'Comment',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'comment' },
        maxWidth: MAX_COLUMN_WIDTH,
      }),
      DataConnector.createGridColumn({
        field: 'validationError',
        headerName: 'Validation Error',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'validationError' },
        maxWidth: MAX_COLUMN_WIDTH,
      }),
    ];
  }
}
